import { ApiError, apiRequest } from '../api/client';

/** Matches `apps/api` `StoredOperatingUnitWithUsage` (`operating-unit.repository.ts`). */
export interface OperatingUnit {
  id: string;
  name: string;
  address: string | null;
  fiscalPointOfSaleId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  fiscalPointOfSale: { id: string; number: number; description: string } | null;
  activeContractsCount: number;
  openOrdersCount: number;
}

/** Matches `apps/api` `OperatingUnitOption` (`GET /operating-units/options`). */
export interface OperatingUnitOption {
  id: string;
  name: string;
}

/** Matches `apps/api` `GET /operating-units` (`settings.manage`). */
export function listOperatingUnits(): Promise<OperatingUnit[]> {
  return apiRequest<OperatingUnit[]>('/operating-units');
}

/** Matches `apps/api` `GET /operating-units/options`: active units within the caller's scope. */
export function listOperatingUnitOptions(): Promise<OperatingUnitOption[]> {
  return apiRequest<OperatingUnitOption[]>('/operating-units/options');
}

/** Matches `apps/api` `createOperatingUnitSchema`. */
export interface CreateOperatingUnitInput {
  name: string;
  address?: string | null;
  fiscalPointOfSaleId?: string | null;
}

/** Matches `apps/api` `POST /operating-units`. */
export function createOperatingUnit(input: CreateOperatingUnitInput): Promise<OperatingUnit> {
  return apiRequest<OperatingUnit>('/operating-units', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `updateOperatingUnitSchema`. */
export interface UpdateOperatingUnitInput {
  name?: string;
  address?: string | null;
  fiscalPointOfSaleId?: string | null;
  isActive?: boolean;
}

/** Matches `apps/api` `PATCH /operating-units/:id`. */
export function updateOperatingUnit(
  id: string,
  input: UpdateOperatingUnitInput,
): Promise<OperatingUnit> {
  return apiRequest<OperatingUnit>(`/operating-units/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** `code` driven, same convention as `mapUserError`; codes come from `OperatingUnitExceptionFilter`. */
export function mapOperatingUnitError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (code === 'OPERATING_UNIT_NAME_DUPLICATE') {
    return 'Ya existe un punto de venta con ese nombre.';
  }

  if (code === 'OPERATING_UNIT_IN_USE') {
    return 'No se puede desactivar: tiene contratos activos o pedidos abiertos.';
  }

  if (code === 'FISCAL_POINT_OF_SALE_ALREADY_LINKED') {
    return 'Ese PV fiscal ya está vinculado a otro punto de venta activo.';
  }

  if (code === 'FISCAL_POINT_OF_SALE_INACTIVE') {
    return 'El PV fiscal elegido está desactivado.';
  }

  if (code === 'FISCAL_POINT_OF_SALE_NOT_FOUND') {
    return 'El PV fiscal elegido ya no existe.';
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 404) {
    return 'El punto de venta ya no existe.';
  }

  if (error.status === 400 || error.status === 422) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}
